import { useAuth } from '@/contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import {
  BookOpen, CheckCircle2, ChevronRight, ArrowRight, Building2, Receipt,
  Users, Calendar, Euro, FileText, Wrench, Shield, Briefcase, Bell, Ban
} from 'lucide-react';

/**
 * Guida operativa role-aware.
 * Mostra una guida diversa in base al ruolo dell'utente sul condominio selezionato.
 * Contenuto allineato alla matrice permessi effettiva (vedi MATRICE-PERMESSI.md).
 */

const ROLE_LABELS = {
  SUPER_ADMIN: 'Super amministratore',
  ADMIN: 'Amministratore di condominio',
  ACCOUNTANT: 'Commercialista / Contabile',
  LABOR_CONSULTANT: 'Consulente del lavoro',
  EMPLOYEE: 'Dipendente / Staff',
  RESIDENT: 'Condòmino',
};

const GUIDES = {
  SUPER_ADMIN: {
    color: 'bg-slate-800',
    intro: 'Gestisci la piattaforma: workspace dei clienti, piani di abbonamento e stato del sistema.',
    can: [
      'Gestire i workspace (studi/clienti) e i relativi abbonamenti',
      'Monitorare lo stato del sistema e gli utenti globali',
      'Accedere ai pannelli di amministrazione della piattaforma',
    ],
    steps: [
      { title: 'Pannello Super Admin', desc: 'Apri la gestione di workspace e piani.', path: '/superadmin' },
      { title: 'Stato del sistema', desc: 'Controlla salute e diagnostica della piattaforma.', path: '/admin/health' },
    ],
    tasks: [
      { icon: Shield, title: 'Gestione workspace', desc: 'Crea/sospendi workspace e gestisci i piani.', path: '/superadmin' },
      { icon: Receipt, title: 'Pagamenti piattaforma', desc: 'Verifica i pagamenti degli abbonamenti.', path: '/admin/payments' },
    ],
    cannot: [],
  },
  ADMIN: {
    color: 'bg-blue-600',
    intro: 'Hai accesso completo al tuo condominio: anagrafiche, contabilità, bilanci, quote, assemblee, comunicazioni e impostazioni.',
    can: [
      'Creare e gestire condomìni, unità e proprietari',
      'Gestire contabilità, spese/fatture, millesimi e ripartizioni',
      'Creare bilanci, generare quote mensili e gestire i pagamenti',
      'Convocare assemblee e redigere i verbali',
      'Inviare comunicazioni e gestire documenti, report ed export',
    ],
    steps: [
      { title: '1. Crea il condominio', desc: 'Dalla Dashboard usa "Nuovo Condominio" e completa i dati.', path: '/dashboard' },
      { title: '2. Aggiungi unità e proprietari', desc: 'Inserisci le unità immobiliari e le persone collegate.', path: '/registry' },
      { title: '3. Imposta i millesimi', desc: 'Configura le tabelle millesimali per i riparti.', path: '/millesimi' },
      { title: '4. Crea il bilancio', desc: 'Imposta preventivo/consuntivo dell’esercizio.', path: '/budgets' },
      { title: '5. Genera le quote mensili', desc: 'Crea i periodi di quota e gli avvisi di pagamento.', path: '/monthly-quotes' },
      { title: '6. Configura i pagamenti', desc: 'Imposta bonifico o pagamento con carta (SumUp).', path: '/settings/payments' },
    ],
    tasks: [
      { icon: Receipt, title: 'Registra una spesa/fattura', desc: 'Contabilità → Spese/Fatture.', path: '/expenses' },
      { icon: Bell, title: 'Invia una comunicazione', desc: 'Pubblica un avviso in bacheca.', path: '/communications' },
      { icon: Calendar, title: 'Convoca un’assemblea', desc: 'Crea la convocazione e poi il verbale.', path: '/meetings' },
      { icon: Building2, title: 'Importa estratti conto', desc: 'Banca → Import e riconciliazione.', path: '/bank-import' },
      { icon: FileText, title: 'Report ed export', desc: 'Genera reportistica ed esportazioni.', path: '/reports' },
    ],
    cannot: [],
  },
  ACCOUNTANT: {
    color: 'bg-emerald-600',
    intro: 'Ti occupi della parte contabile e fiscale: spese, fatture, bilanci, ritenute e documenti.',
    can: [
      'Visualizzare anagrafiche di unità e persone',
      'Registrare e gestire spese, fatture e fornitori',
      'Lavorare su millesimi, ripartizioni e bilanci',
      'Gestire ritenute/770 e generare report ed export',
      'Caricare e consultare documenti',
    ],
    steps: [
      { title: '1. Seleziona il condominio', desc: 'Scegli in alto il condominio su cui lavorare.', path: '/dashboard' },
      { title: '2. Controlla spese e fatture', desc: 'Verifica e registra i movimenti contabili.', path: '/expenses' },
      { title: '3. Prepara il bilancio', desc: 'Aggiorna preventivo/consuntivo dell’esercizio.', path: '/budgets' },
    ],
    tasks: [
      { icon: Receipt, title: 'Registra fatture', desc: 'Contabilità → Spese/Fatture.', path: '/expenses' },
      { icon: Building2, title: 'Riconciliazione bancaria', desc: 'Abbina i movimenti agli incassi/spese.', path: '/reconciliation' },
      { icon: FileText, title: 'Ritenute / 770', desc: 'Gestisci la parte fiscale.', path: '/tax' },
      { icon: FileText, title: 'Report ed export', desc: 'Produci la documentazione contabile.', path: '/reports' },
    ],
    cannot: ['Creare/eliminare il condominio', 'Gestire impostazioni, branding e personale', 'Vedere l’audit log'],
  },
  LABOR_CONSULTANT: {
    color: 'bg-indigo-600',
    intro: 'Gestisci il personale del condominio (portierato): buste paga, F24 e presenze.',
    can: [
      'Consultare l’elenco del personale',
      'Caricare buste paga e documenti F24',
      'Esportare il riepilogo di presenze e assenze',
      'Vedere il condominio e le comunicazioni',
    ],
    steps: [
      { title: '1. Apri il modulo Personale', desc: 'Vai alla sezione dedicata ai dipendenti.', path: '/staff/consultant' },
      { title: '2. Carica i documenti HR', desc: 'Buste paga e F24 del personale.', path: '/staff/consultant' },
    ],
    tasks: [
      { icon: Briefcase, title: 'Buste paga & F24', desc: 'Carica e gestisci i documenti del personale.', path: '/staff/consultant' },
      { icon: Users, title: 'Riepilogo presenze', desc: 'Esporta presenze e assenze mensili.', path: '/staff/consultant' },
    ],
    cannot: ['Contabilità, bilanci e quote', 'Anagrafiche unità/persone', 'Impostazioni del condominio'],
  },
  EMPLOYEE: {
    color: 'bg-teal-600',
    intro: 'Questo è il tuo portale personale come dipendente del condominio.',
    can: [
      'Consultare il tuo contratto e i tuoi documenti',
      'Vedere le tue presenze',
      'Inviare richieste (es. permessi)',
    ],
    steps: [
      { title: '1. Apri il tuo portale', desc: 'Accedi all’area dipendente.', path: '/staff/my' },
      { title: '2. Controlla presenze e documenti', desc: 'Verifica i tuoi dati e invia richieste.', path: '/staff/my' },
    ],
    tasks: [
      { icon: Briefcase, title: 'Il mio portale', desc: 'Contratto, presenze e richieste.', path: '/staff/my' },
    ],
    cannot: ['Accesso ai dati di altri dipendenti', 'Contabilità e gestione del condominio'],
  },
  RESIDENT: {
    color: 'bg-amber-600',
    intro: 'Da qui consulti i tuoi dati, paghi le quote, leggi le comunicazioni e segnali eventuali guasti.',
    can: [
      'Vedere le informazioni della tua unità e i tuoi importi',
      'Consultare e pagare le quote (online o caricando la ricevuta)',
      'Leggere comunicazioni, documenti e verbali di assemblea',
      'Segnalare guasti e disservizi',
    ],
    steps: [
      { title: '1. Guarda la Dashboard', desc: 'Hai una sintesi della tua situazione.', path: '/dashboard' },
      { title: '2. Controlla le quote', desc: 'Vedi quanto devi e le scadenze.', path: '/monthly-quotes' },
      { title: '3. Paga o carica la ricevuta', desc: 'Paga con carta/bonifico o allega la ricevuta del pagamento.', path: '/monthly-quotes' },
      { title: '4. Leggi le comunicazioni', desc: 'Resta aggiornato sugli avvisi del condominio.', path: '/my-communications' },
    ],
    tasks: [
      { icon: Euro, title: 'Paga una quota', desc: 'Quote Mensili → paga o carica ricevuta.', path: '/monthly-quotes' },
      { icon: FileText, title: 'Scarica un documento', desc: 'Consulta l’archivio documenti.', path: '/documents' },
      { icon: Wrench, title: 'Segnala un guasto', desc: 'Apri una segnalazione al condominio.', path: '/incidents' },
      { icon: Calendar, title: 'Verbali di assemblea', desc: 'Leggi le decisioni delle assemblee.', path: '/meetings' },
    ],
    cannot: ['Modificare dati del condominio o di altre unità', 'Accedere a contabilità, fornitori e impostazioni'],
  },
};

export const Guida = () => {
  const { selectedCondominium } = useAuth();
  const navigate = useNavigate();
  const role = selectedCondominium?.role || 'RESIDENT';
  const condoName = selectedCondominium?.condominium?.name || 'Condominio';
  const guide = GUIDES[role] || GUIDES.RESIDENT;
  const roleLabel = ROLE_LABELS[role] || role;

  return (
    <div className="space-y-6 max-w-4xl" data-testid="guida">
      {/* Header */}
      <div className="flex items-start gap-4">
        <div className={`p-3 rounded-xl ${guide.color}`}>
          <BookOpen className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="font-heading text-2xl sm:text-3xl font-bold tracking-tight text-slate-900">
            Guida operativa
          </h1>
          <p className="text-sm sm:text-base text-stone-600 mt-1">
            {condoName} · <Badge variant="secondary">{roleLabel}</Badge>
          </p>
          <p className="text-sm text-stone-600 mt-2">{guide.intro}</p>
        </div>
      </div>

      {/* Cosa puoi fare */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2 text-lg">
            <CheckCircle2 className="h-5 w-5 text-emerald-600" />
            Cosa puoi fare
          </CardTitle>
        </CardHeader>
        <CardContent>
          <ul className="space-y-2">
            {guide.can.map((item, i) => (
              <li key={i} className="flex items-start gap-2 text-sm text-slate-700">
                <ChevronRight className="h-4 w-4 mt-0.5 text-emerald-600 flex-shrink-0" />
                <span>{item}</span>
              </li>
            ))}
          </ul>
        </CardContent>
      </Card>

      {/* Primi passi */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Primi passi</CardTitle>
          <CardDescription>Segui questi passaggi per iniziare.</CardDescription>
        </CardHeader>
        <CardContent>
          <ol className="space-y-3">
            {guide.steps.map((step, i) => (
              <li key={i} className="flex items-start gap-3">
                <span className="flex h-6 w-6 flex-shrink-0 items-center justify-center rounded-full bg-slate-100 text-xs font-semibold text-slate-700">
                  {i + 1}
                </span>
                <div className="flex-1">
                  <p className="text-sm font-medium text-slate-900">{step.title}</p>
                  <p className="text-sm text-stone-600">{step.desc}</p>
                </div>
                {step.path && (
                  <Button variant="ghost" size="sm" onClick={() => navigate(step.path)} className="flex-shrink-0">
                    Vai <ArrowRight className="h-4 w-4 ml-1" />
                  </Button>
                )}
              </li>
            ))}
          </ol>
        </CardContent>
      </Card>

      {/* Operazioni frequenti */}
      {guide.tasks?.length > 0 && (
        <div>
          <h2 className="text-lg font-semibold text-slate-800 mb-3">Operazioni frequenti</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            {guide.tasks.map((task, i) => {
              const Icon = task.icon || FileText;
              return (
                <button
                  key={i}
                  onClick={() => task.path && navigate(task.path)}
                  className="text-left p-4 rounded-lg border bg-white hover:shadow-md hover:border-slate-300 transition-all"
                >
                  <div className="flex items-start gap-3">
                    <div className="p-2 rounded-full bg-slate-50">
                      <Icon className="h-4 w-4 text-slate-700" />
                    </div>
                    <div className="flex-1 min-w-0">
                      <p className="text-sm font-medium text-slate-900">{task.title}</p>
                      <p className="text-xs text-stone-600 mt-0.5">{task.desc}</p>
                    </div>
                    <ChevronRight className="h-4 w-4 text-slate-400 flex-shrink-0" />
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Cosa non puoi fare */}
      {guide.cannot?.length > 0 && (
        <Card className="border-stone-200 bg-stone-50">
          <CardHeader>
            <CardTitle className="flex items-center gap-2 text-base text-stone-700">
              <Ban className="h-4 w-4 text-stone-500" />
              Non rientra nel tuo ruolo
            </CardTitle>
          </CardHeader>
          <CardContent>
            <ul className="space-y-1.5">
              {guide.cannot.map((item, i) => (
                <li key={i} className="text-sm text-stone-600">• {item}</li>
              ))}
            </ul>
          </CardContent>
        </Card>
      )}

      {/* Footer help */}
      <div className="flex flex-wrap gap-2 pt-2">
        <Button variant="outline" size="sm" onClick={() => navigate('/faq')}>
          Vedi le FAQ
        </Button>
        <Button
          variant="outline"
          size="sm"
          onClick={() => (window.location.href = 'mailto:support.app@artegosrls.it')}
        >
          Contatta il supporto
        </Button>
      </div>
    </div>
  );
};

export default Guida;
